import React, { type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Avatar from '@atlaskit/avatar';
import LockIcon from '@atlaskit/icon/core/lock-locked';
import FolderIcon from '@atlaskit/icon/core/folder-closed';
import DatabaseIcon from '@atlaskit/icon/core/database';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import PageIcon from '@atlaskit/icon/core/page';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';

import { SpaceIcon } from '@confluence/icons/entry-points/SpaceIcon.ts';

import type { UserPrincipal } from '../commonTypes.tsx';

import type { BlockedAccessRestrictionsQuery_blockedAccessRestrictions_blockedAccessRestrictionSummary_restrictedResources } from './queries/__types__/BlockedAccessRestrictionsQuery.ts';
import type { ResourceType } from './queries/__generated__/BlockedAccessRestrictionsQuery.graphql.ts';

export type RestrictedUserResourcesDetailsProps = {
	user: UserPrincipal | undefined;
	restrictedResources:
		| ReadonlyArray<
				| BlockedAccessRestrictionsQuery_blockedAccessRestrictions_blockedAccessRestrictionSummary_restrictedResources
				| undefined
				| null
		  >
		| null
		| undefined;
};

const i18n = defineMessages({
	needsAccess: {
		id: 'share-and-restrict-dialog.restricted-user-resources-details.needs-access',
		defaultMessage:
			'needs access in {resourcesCount} {resourcesCount, plural, one {place} other {places}}:',
		description:
			'Label indicating that the user is the current user, shown as "Jane Rotanson (You)"',
	},
	space: {
		id: 'share-and-restrict-dialog.restricted-user-resources-details.space',
		defaultMessage: 'Space',
		description:
			'The text is shown as a column header or label in the restricted resources details section of the resolve restrictions dialog, identifying the space column where the restricted space name is displayed.',
	},
	parentItems: {
		id: 'share-and-restrict-dialog.restricted-user-resources-details.parent-items',
		defaultMessage: 'Parent items',
		description: 'Label to show list of restricted parents',
	},
});

export type SingleResourcesDetailsProps = {
	restrictedResource:
		| BlockedAccessRestrictionsQuery_blockedAccessRestrictions_blockedAccessRestrictionSummary_restrictedResources
		| null
		| undefined;
	showLock: boolean;
};

const getContenTypeIcon = (type: ResourceType) => {
	switch (type) {
		case 'DATABASE':
			return <DatabaseIcon label="Databse" />;
		case 'WHITEBOARD':
			return <WhiteboardIcon label="Whiteboard" />;
		case 'PAGE':
			return <PageIcon label="Page" />;
		case 'FOLDER':
			return <FolderIcon label="Page" />;
		case 'SPACE':
			return <SpaceIcon size="xsmall" src="/images/logo/default-space-logo-256.png" />;
	}
};

const SingleResourcesDetails: FC<SingleResourcesDetailsProps> = ({
	restrictedResource,
	showLock,
}) => {
	return (
		<Inline space="space.150" alignBlock="center">
			{showLock ? <LockIcon label="" color={token('color.icon.danger')} /> : null}
			{restrictedResource && getContenTypeIcon(restrictedResource.resourceType)}

			<Text color="color.text.subtle">
				{restrictedResource ? restrictedResource.resourceTitle : ''}
			</Text>
		</Inline>
	);
};

export const RestrictedUserResourcesDetails: FC<RestrictedUserResourcesDetailsProps> = ({
	user,
	restrictedResources,
}) => {
	const resourcesWithoutSpace =
		restrictedResources &&
		restrictedResources.filter((resource) => {
			if (resource?.resourceType === 'SPACE') {
				return false;
			}
			return true;
		});
	const restrictedSpaces =
		restrictedResources &&
		restrictedResources.filter((resource) => {
			if (resource?.resourceType === 'SPACE') {
				return true;
			}
			return false;
		});

	const resourcesWithoutSpaceCount = resourcesWithoutSpace ? resourcesWithoutSpace.length : 0;
	const restrictedSpacesCount = restrictedSpaces ? restrictedSpaces.length : 0;

	const resourcesCount = resourcesWithoutSpaceCount + restrictedSpacesCount;

	return (
		<Stack space="space.150">
			<Inline space="space.050" alignBlock="center">
				<Avatar src={user && user.avatarUrl} />
				<Text color="color.text" weight="bold">
					{user && user.name}
				</Text>
				<FormattedMessage values={{ resourcesCount }} {...i18n.needsAccess} />
			</Inline>
			<Box paddingInlineStart="space.500">
				<Stack space="space.200" alignBlock="center">
					{restrictedSpaces && restrictedSpaces.length > 0 ? (
						<Stack space="space.100" alignBlock="center">
							<Heading size="xxsmall" as="h2">
								<FormattedMessage {...i18n.space} />
							</Heading>
							<Box paddingInlineStart="space.150">
								{restrictedSpaces.map((resource) => (
									<SingleResourcesDetails
										restrictedResource={resource}
										showLock={false}
										key={resource?.resourceId}
									/>
								))}
							</Box>
						</Stack>
					) : null}
					{resourcesWithoutSpace && resourcesWithoutSpace.length > 0 ? (
						<Stack space="space.200" alignBlock="center">
							<Heading size="xxsmall" as="h2">
								<FormattedMessage {...i18n.parentItems} />
							</Heading>
							<Box paddingInlineStart="space.150">
								<Stack space="space.200" alignBlock="center">
									{resourcesWithoutSpace.map((resource) => (
										<SingleResourcesDetails
											restrictedResource={resource}
											showLock
											key={resource?.resourceId}
										/>
									))}
								</Stack>
							</Box>
						</Stack>
					) : null}
				</Stack>
			</Box>
		</Stack>
	);
};
